import { motion } from "framer-motion";
import { CTAButton } from "../common/CTAButton";
import { cn } from "../../utils/cn";

type HeroSectionProps = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: { label: string; href: string };
  secondaryCta: { label: string; href: string };
  proofPoints: string[];
  backgroundImage: string;
};

export function HeroSection({
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  proofPoints,
  backgroundImage,
}: HeroSectionProps) {
  return (
    <section
      className="relative isolate overflow-hidden bg-slate-950"
      aria-labelledby="hero-heading"
    >
      <div
        className="absolute inset-0"
        aria-hidden="true"
        role="presentation"
      >
        <img
          src={backgroundImage}
          alt="Mission-critical operations command center"
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-950/90 to-slate-950" />
      </div>
      <div className="relative mx-auto max-w-6xl px-6 py-32 sm:py-40">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center rounded-full bg-emerald-400/10 px-4 py-1 text-xs font-medium uppercase tracking-[0.3em] text-emerald-300"
          >
            {eyebrow}
          </motion.span>
          <motion.h1
            id="hero-heading"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-8 text-balance text-4xl font-semibold text-white sm:text-5xl lg:text-6xl"
            style={{
              opacity: "1",
              transform: "none",
              fontWeight: "normal"
            }}>
            {title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 text-lg text-slate-300 sm:text-xl"
          >
            {description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <CTAButton href={primaryCta.href}>{primaryCta.label}</CTAButton>
            <CTAButton href={secondaryCta.href} variant="secondary">
              {secondaryCta.label}
            </CTAButton>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1, delayChildren: 0.6 },
              },
            }}
            className="mt-12 grid gap-3 sm:grid-cols-3"
          >
            {proofPoints.map((point) => (
              <motion.div
                key={point}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                className={cn(
                  "rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 backdrop-blur",
                  "hover:border-emerald-400/40 hover:text-white"
                )}
              >
                {point}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
